/**
 * Place a signature stamp onto a Fabric overlay canvas.
 */

import type { fabric as FabricNS } from 'fabric';
import fabric from '@/lib/fabric';
import type { PdfSignature } from '@canvas/shared-types';
import { resolveSignatureBlob } from '@/lib/pdf/signatureStore';

/**
 * Synchronous string preview URL for the signatures rail.
 * Meta often has no dataUrl after localStorage stripping — SignatureThumb
 * should prefer createObjectURL from the blob cache / resolveSignatureBlob.
 */
export function signatureRenderUrl(sig: PdfSignature): string {
  if (sig.dataUrl?.startsWith('data:image/')) return sig.dataUrl;
  if (sig.downloadUrl?.startsWith('data:image/')) return sig.downloadUrl;
  if (sig.downloadUrl && !sig.downloadUrl.startsWith('data:')) return sig.downloadUrl;
  return '';
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string' || !result.startsWith('data:image/')) {
        reject(new Error('Failed to encode signature image'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error('Failed to encode signature image'));
    reader.readAsDataURL(blob);
  });
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // data: URLs don't need CORS; remote URLs do for canvas export
    if (!src.startsWith('data:') && !src.startsWith('blob:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      if (!img.naturalWidth || !img.naturalHeight) {
        reject(new Error('Signature image is empty'));
        return;
      }
      resolve(img);
    };
    img.onerror = () => reject(new Error('Failed to load signature image'));
    img.src = src;
  });
}

export async function addSignatureToFabric(options: {
  canvas: FabricNS.Canvas;
  signature: PdfSignature;
  left: number;
  top: number;
  maxWidthRatio?: number;
  /** When false, stamp is placed without becoming the active object. */
  activate?: boolean;
}): Promise<FabricNS.Image> {
  const {
    canvas,
    signature,
    left,
    top,
    maxWidthRatio = 0.35,
    activate = true,
  } = options;

  const blob = await resolveSignatureBlob(signature);
  // Must use a durable data: URL — blob: URLs were revoked after place and
  // Finalize's loadFromJSON could not reload the signature images.
  const dataUrl = await blobToDataUrl(blob);
  const imgEl = await loadHtmlImage(dataUrl);

  const maxW = Math.max(48, canvas.getWidth() * maxWidthRatio);
  const scale = Math.min(1, maxW / imgEl.naturalWidth);

  const img = new fabric.Image(imgEl, {
    left,
    top,
    originX: 'left',
    originY: 'top',
    scaleX: scale,
    scaleY: scale,
    name: 'pdf-sign',
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    lockMovementX: false,
    lockMovementY: false,
    // Caching on: objectCaching:false redraws controls onto a dirty upper canvas on iOS
    objectCaching: true,
  });

  canvas.add(img);
  if (activate) canvas.setActiveObject(img);
  canvas.requestRenderAll();
  return img;
}
